import { Instant, ZonedDateTime } from "@js-joda/core";
import { useSet } from "@uidotdev/usehooks";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
	type ContentFileDto,
	ContentType,
	type CreatePostDto,
	canApprove,
	DEFAULT_YOUTUBE_CATEGORY_ID,
	getSocialConstraints,
	Platform,
	PlatformEnum,
	type PlatformValue,
	type PostDto,
	PostStatus,
	postCopyContainsUrl,
	postLabel,
	TIKTOK_PRIVACY_LEVELS,
	tiktokPublishBlockReason,
	validateTikTokMedia,
	validateYouTubeHashtagCount,
	validateYouTubeMedia,
	validateYouTubeThumbnailDimensions,
	validateYouTubeTitleInput,
	type YouTubeMediaFileInfo,
	type YouTubeVideoMetadata,
	youtubePrivacyLabel,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import { Api } from "../api.config";
import {
	useBillingEntitlements,
	useBillingPlanPaidCheck,
} from "../billing/useSubscription";
import { usePostsActions } from "../calendar/usePosts";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { type UseFormReturn, useForm } from "../common/form/useForm";
import { useModal } from "../common/modal/Modal";
import { useCompany } from "../company/useCompany";
import { useMediaActions } from "../content/useMediaActions";
import {
	useSuspenseMedia,
	useSuspenseMediaList,
} from "../content/useMediaBatch";
import { LinkGen } from "../LinkGen";
import { useUploadComplete } from "../media/upload";
import { ModalConfirmation } from "../settings/ModalConfirmation";
import {
	useTikTok,
	useWordpress,
	useYouTube,
} from "../settings/useIntegrations";
import { ModalPaywall } from "./ModalPaywall";
import {
	ModalSchedulePost,
	type SchedulePostSummary,
} from "./ModalSchedulePost";
import { ModalSelectMedia } from "./ModalSelectMedia";
import {
	readImageDimensionsFromUrl,
	readVideoMetadataFromUrl,
} from "./readVideoMetadata";

type FormPostContextType = {
	form: UseFormReturn<CreatePostDto>;
	platforms: Set<Platform | PlatformValue>;
	selectedPlatforms: Array<Platform>;
	selectedFiles: Array<ContentFileDto>;
	selectedThumbnail?: ContentFileDto;
	isPlanPaid: boolean;
	post?: PostDto;
	siblings?: Array<PostDto>;
	editing?: boolean;
	isBlog: boolean;
	needsApproval: boolean;
	isShorts: boolean;
	/** When set, Schedule / Publish Now should be disabled (TikTok UX rules). */
	tiktokPublishBlockedReason?: string;
	handleSaveDraftClick: () => Promise<void>;
	handlePlatformChange: (value: Platform | PlatformValue) => void;
	handleScheduleClick: () => void;
	handlePublishClick: () => void;
	handleAttachContent: () => void;
	handleRemoveContent: (id: number) => void;
	handleReorderFiles: (fromIndex: number, toIndex: number) => void;
	handleSelectThumbnail: () => void;
	handleRemoveThumbnail: () => void;
	handleSaveClick: (id: number) => Promise<boolean>;
	isSaving: boolean;
	handleDeleteClick: (id: number) => void;
	handleSubmitForApproval: () => Promise<void>;
	handleApprovePost: () => Promise<void>;
	handleRejectPost: (reason?: string) => Promise<void>;
};

export const FormPostContext = createContext<FormPostContextType | null>(null);

type PostProviderProps = {
	post?: PostDto;
	siblings?: Array<PostDto>;
	editing?: boolean;
	isBlog?: boolean;
	isShorts?: boolean;
};
export const FormPostProvider: FC<
	Readonly<PropsWithChildren<PostProviderProps>>
> = ({
	post,
	siblings,
	editing,
	children,
	isBlog = false,
	isShorts: isShortsProp = false,
}) => {
	const { update, remove, create, submitForApproval, approvePost, rejectPost } =
		usePostsActions();
	const navigate = useNavigate();
	const ids = useSuspenseMediaList();
	const media = useSuspenseMedia(ids);
	const emit = useAlertEmit();
	const { open, confirm, close } = useModal();
	const platforms = useSet<Platform | PlatformValue>([]);
	const isPlanPaid = useBillingPlanPaidCheck();
	const { data: entitlements } = useBillingEntitlements();
	const { status: wpStatus } = useWordpress();
	const company = useCompany();
	const { status: youtubeStatus } = useYouTube();
	const { status: tiktokStatus } = useTikTok();

	const isApprover = company.myRole ? canApprove(company.myRole) : false;
	// When requirePostApproval is ON, non-approvers (MEMBER) need to submit posts for approval
	const needsApproval = company.requirePostApproval && !isApprover;

	const [searchParams] = useSearchParams();
	const location = useLocation();
	const initialMediaIdsParam = searchParams.get("mediaIds");
	const initialMediaId = searchParams.get("mediaId");
	const queryMediaIds = useMemo(
		() => parseQueryMediaIds(initialMediaIdsParam, initialMediaId),
		[initialMediaIdsParam, initialMediaId],
	);
	const generatedContent = generatedContentFromState(location.state);
	const platformParam = searchParams.get("platform");
	const platformFromQuery = parsePlatformQueryParam(platformParam);
	const isShorts =
		isShortsProp ||
		(!editing && !post && platformFromQuery === Platform.YOUTUBE);

	const initial: CreatePostDto = useMemo(
		() =>
			post
				? ({
						subject: post.subject,
						content: post.content,
						platform: [post.platform],
						scheduledAt: post.scheduledAt,
						files: post.files,
						publish: false,
						tiktokPrivacyLevel: post.tiktokPrivacyLevel,
						tiktokAllowComment: post.tiktokAllowComment === true,
						tiktokAllowDuet: post.tiktokAllowDuet === true,
						tiktokAllowStitch: post.tiktokAllowStitch === true,
						tiktokCommercialDisclosure:
							post.tiktokCommercialDisclosure === true,
						tiktokBrandOrganic: post.tiktokBrandOrganic === true,
						tiktokBrandedContent: post.tiktokBrandedContent === true,
						youtubePrivacyStatus: post.youtubePrivacyStatus ?? "public",
						youtubeCategoryId:
							post.youtubeCategoryId ?? DEFAULT_YOUTUBE_CATEGORY_ID,
						youtubeThumbnailFileId: post.youtubeThumbnailFileId,
						youtubeChannelId: post.youtubeChannelId,
					} satisfies CreatePostDto)
				: ({
						subject: "",
						content: generatedContent,
						platform: isShorts
							? [Platform.YOUTUBE]
							: platformFromQuery
								? [platformFromQuery]
								: ([] as Array<Platform>),
						scheduledAt: undefined,
						files: queryMediaIds.length ? [...queryMediaIds] : undefined,
						publish: false,
						tiktokPrivacyLevel: undefined,
						tiktokAllowComment: false,
						tiktokAllowDuet: false,
						tiktokAllowStitch: false,
						tiktokCommercialDisclosure: false,
						tiktokBrandOrganic: false,
						tiktokBrandedContent: false,
						youtubePrivacyStatus: "public",
						youtubeCategoryId: DEFAULT_YOUTUBE_CATEGORY_ID,
						youtubeThumbnailFileId: undefined,
						youtubeChannelId: youtubeStatus.data.profile?.id,
					} satisfies CreatePostDto),
		[
			post,
			isShorts,
			platformFromQuery,
			youtubeStatus.data.profile?.id,
			queryMediaIds,
			generatedContent,
		],
	);

	const form = useForm({
		initialValues: initial,

		onSubmit: async (values) => {
			if (isBlog && values.publish) {
				if (
					post?.platform !== PlatformEnum.WORDPRESS ||
					!wpStatus.data.connected
				) {
					handleNotIntegrated();
					return;
				}
			}
			if (editing && post) {
				await update.mutateAsync({
					id: post.id,
					dto: { ...values, platform: values.platform, files: values.files },
				});

				return navigate(-1);
			}
			if (!editing && !post) {
				await create.mutateAsync(values);
				return navigate(-1);
			}
		},
	});

	useEffect(() => {
		if (editing || post) return;
		if (isShorts) {
			platforms.add(Platform.YOUTUBE);
			form.setFieldValue("platform", [Platform.YOUTUBE]);
			return;
		}
		if (platformFromQuery) {
			platforms.add(platformFromQuery);
			form.setFieldValue("platform", [platformFromQuery]);
		}
	}, [isShorts, editing, post, platformFromQuery, platforms, form]);

	const selectedFiles = useMemo(() => {
		const byId = new Map(media.map((file) => [file.id, file]));
		return (form.values.files ?? [])
			.map((id) => byId.get(id))
			.filter((file): file is ContentFileDto => Boolean(file));
	}, [media, form.values.files]);
	const selectedThumbnail = useMemo(
		() =>
			form.values.youtubeThumbnailFileId
				? media.find((f) => f.id === form.values.youtubeThumbnailFileId)
				: undefined,
		[media, form.values.youtubeThumbnailFileId],
	);

	const selectedPlatforms = useMemo(
		() => (editing && post ? [post.platform] : form.values.platform),
		[editing, post, form.values.platform],
	);

	const tiktokPrivacyOptions = useMemo(() => {
		const fromApi = tiktokStatus.data.privacyLevelOptions;
		if (fromApi && fromApi.length > 0) {
			return fromApi;
		}
		return [...TIKTOK_PRIVACY_LEVELS];
	}, [tiktokStatus.data.privacyLevelOptions]);

	const tiktokPublishBlockedReason = useMemo(() => {
		if (!selectedPlatforms.includes(Platform.TIKTOK)) {
			return undefined;
		}
		return tiktokPublishBlockReason(
			{
				privacyLevel: form.values.tiktokPrivacyLevel,
				allowComment: form.values.tiktokAllowComment === true,
				allowDuet: form.values.tiktokAllowDuet === true,
				allowStitch: form.values.tiktokAllowStitch === true,
				commercialDisclosure: form.values.tiktokCommercialDisclosure === true,
				brandOrganic: form.values.tiktokBrandOrganic === true,
				brandedContent: form.values.tiktokBrandedContent === true,
			},
			tiktokPrivacyOptions,
		);
	}, [
		selectedPlatforms,
		form.values.tiktokPrivacyLevel,
		form.values.tiktokAllowComment,
		form.values.tiktokAllowDuet,
		form.values.tiktokAllowStitch,
		form.values.tiktokCommercialDisclosure,
		form.values.tiktokBrandOrganic,
		form.values.tiktokBrandedContent,
		tiktokPrivacyOptions,
	]);

	const youtubeChannels = useMemo(
		() =>
			youtubeStatus.data.channels ??
			(youtubeStatus.data.profile ? [youtubeStatus.data.profile] : []),
		[youtubeStatus.data.channels, youtubeStatus.data.profile],
	);

	const selectedYoutubeChannel = useMemo(() => {
		const channelId =
			form.values.youtubeChannelId ?? youtubeStatus.data.profile?.id;
		return (
			youtubeChannels.find((channel) => channel.id === channelId) ??
			youtubeStatus.data.profile
		);
	}, [
		form.values.youtubeChannelId,
		youtubeChannels,
		youtubeStatus.data.profile,
	]);

	const handleSaveClick = useEventCallback(async (id: number) => {
		const savedValues = {
			...form.values,
			platform: form.values.platform,
			files: form.values.files,
		};
		try {
			await update.mutateAsync({
				id: id,
				dto: savedValues,
			});

			emit({
				type: AlertType.SUCCESS,
				content: "Post saved.",
			});

			return true;
		} catch (err) {
			const error = err as {
				response?: {
					data?: { message?: string; errors?: Record<string, string[]> };
				};
			};
			const errMsg = error.response?.data?.message;
			const errStack = error.response?.data?.errors;

			if (errStack) {
				for (const k of Object.keys(errStack)) {
					form.setFieldError(k, errStack[k].join(". "));
				}
			}

			emit({
				type: AlertType.ERROR,
				content: `Failed to update post.${errMsg ? ` ${errMsg}` : ""}`,
			});

			return false;
		}
	});

	const handleSaveDraftClick = useEventCallback(async () => {
		create
			.mutateAsync({
				...form.values,
				platform: form.values.platform,
				files: form.values.files,
			})
			.then(() => {
				navigate(LinkGen.Calendar());
			})
			.catch((err) => {
				const errMsg = err.response?.data?.message as string | undefined;
				const errStack = err.response?.data?.errors as
					| Record<string, string[]>
					| undefined;

				if (errStack) {
					const stackKeys = Object.keys(errStack || {}) ?? [];

					for (const k of stackKeys) {
						form.setFieldError(k, errStack[k].join(". "));
					}
				}

				emit({
					type: AlertType.ERROR,
					content: `Failed to create post.${errMsg ? ` ${errMsg}` : ""}`,
				});
			});
	});

	const handleDeleteClick = useEventCallback((id: number) => {
		confirm({
			children: (
				<>
					Are you sure you want to delete{" "}
					<strong>{postLabel(form.values)}</strong>?
				</>
			),
			confirmText: "Confirm deletion",
			onConfirm: async () => {
				await remove.mutateAsync(id);
				navigate(LinkGen.Calendar());
			},
			variant: "danger",
		});
	});

	const { invalidateMedia } = useMediaActions();

	const isYouTubeSelected =
		isShorts || selectedPlatforms.includes(Platform.YOUTUBE);

	useUploadComplete(async (mediaID) => {
		if (!isYouTubeSelected) {
			return;
		}
		await invalidateMedia();
		try {
			const file = (await Api.GetMediaBatch([mediaID])).data[0];
			if (file?.type === ContentType.VIDEO) {
				form.setFieldValue("files", [mediaID]);
			}
		} catch {
			// Library invalidate still ran; user can attach manually if fetch fails
		}
	});

	const handleAttachContent = useEventCallback(() => {
		open(ModalSelectMedia, {
			values: form.values?.files ?? [],
			multiple: !isBlog && !isYouTubeSelected,
			uploadMode: isYouTubeSelected ? "youtubeShorts" : "default",
			acceptType: isYouTubeSelected ? ContentType.VIDEO : undefined,
			onChange: (ids?: Array<number>) => {
				form.setFieldValue(
					"files",
					isYouTubeSelected && ids?.length ? [ids[0]] : ids,
				);
			},
		});
	});

	const handleRemoveContent = useEventCallback((id: number) => {
		form.setFieldValue(
			"files",
			form.values.files?.filter((f) => f !== id),
		);
	});

	const handleReorderFiles = useEventCallback(
		(fromIndex: number, toIndex: number) => {
			const current = [...(form.values.files ?? [])];
			if (
				fromIndex === toIndex ||
				fromIndex < 0 ||
				toIndex < 0 ||
				fromIndex >= current.length ||
				toIndex >= current.length
			) {
				return;
			}
			const [moved] = current.splice(fromIndex, 1);
			current.splice(toIndex, 0, moved);
			form.setFieldValue("files", current);
		},
	);

	const handleSelectThumbnail = useEventCallback(() => {
		open(ModalSelectMedia, {
			values: form.values.youtubeThumbnailFileId
				? [form.values.youtubeThumbnailFileId]
				: [],
			multiple: false,
			onChange: async (ids?: Array<number>) => {
				const id = ids?.[0];
				if (!id) {
					form.setFieldValue("youtubeThumbnailFileId", undefined);
					return;
				}

				try {
					const file = (await Api.GetMediaBatch([id])).data[0];
					if (!file) {
						emit({
							type: AlertType.ERROR,
							content: "Thumbnail file not found.",
						});
						return;
					}
					const dims = await readImageDimensionsFromUrl(file.url);
					const thumbCheck = validateYouTubeThumbnailDimensions(
						dims.width,
						dims.height,
					);
					if (!thumbCheck.valid) {
						emit({
							type: AlertType.ERROR,
							content: thumbCheck.errors.join(" "),
						});
						return;
					}
					form.setFieldValue("youtubeThumbnailFileId", id);
				} catch {
					emit({
						type: AlertType.ERROR,
						content: "Could not read thumbnail dimensions.",
					});
				}
			},
		});
	});

	const handleRemoveThumbnail = useEventCallback(() => {
		form.setFieldValue("youtubeThumbnailFileId", undefined);
	});

	const handlePlatformChange = useCallback(
		(value: Platform | PlatformValue) => {
			if (isShorts) {
				return;
			}
			if (platforms.has(value)) {
				platforms.delete(value);
				form.setFieldValue("platform", Array.from(platforms));
				return;
			}

			platforms.add(value);
			form.setFieldValue("platform", Array.from(platforms));
		},
		[platforms, form.setFieldValue, isShorts],
	);

	const handleNotIntegrated = useEventCallback(() => {
		open(ModalConfirmation, {
			confirmText: "Copy",
			prompt:
				"You will need to copy and paste this post into your website for the blog to be published as we do not currently integrate with your website technology.. Do you want to copy post to the clipboard?",
			onConfirm: async () => {
				navigator.clipboard.writeText(form.values.content);
				close();
			},
		});
	});

	const collectYouTubeVideoMetadata = useCallback(async (): Promise<
		YouTubeVideoMetadata | undefined
	> => {
		const videoFile = selectedFiles.find((f) => f.type === "video");
		if (!videoFile) {
			return undefined;
		}
		try {
			const meta = await readVideoMetadataFromUrl(videoFile.url);
			return {
				width: meta.width,
				height: meta.height,
				durationSec: meta.durationSec,
			};
		} catch {
			return undefined;
		}
	}, [selectedFiles]);

	const validateYouTubeBeforePublish = useCallback(async () => {
		if (!selectedPlatforms.includes(Platform.YOUTUBE)) {
			return true;
		}

		const titleCheck = validateYouTubeTitleInput(
			form.values.subject,
			form.values.content,
		);
		if (!titleCheck.valid) {
			emit({
				type: AlertType.ERROR,
				content: titleCheck.errors.join(" "),
			});
			return false;
		}

		const hashtagCheck = validateYouTubeHashtagCount(form.values.content ?? "");
		if (!hashtagCheck.valid) {
			emit({
				type: AlertType.ERROR,
				content: hashtagCheck.errors.join(" "),
			});
			return false;
		}

		const videoFiles = selectedFiles.filter((f) => f.type === "video");
		const fileInfos: YouTubeMediaFileInfo[] = selectedFiles.map((f) => ({
			extension: f.extension,
			size: f.size,
			type: f.type,
			filename: f.filename,
		}));

		for (const file of videoFiles) {
			try {
				const meta = await readVideoMetadataFromUrl(file.url);
				const info = fileInfos.find((f) => f.filename === file.filename);
				if (info) {
					info.width = meta.width;
					info.height = meta.height;
					info.durationSec = meta.durationSec;
				}
			} catch {
				// Metadata unavailable — validateYouTubeMedia will warn
			}
		}

		const youtubeCheck = validateYouTubeMedia(fileInfos);
		if (!youtubeCheck.valid) {
			emit({
				type: AlertType.ERROR,
				content: youtubeCheck.errors.join(" "),
			});
			return false;
		}

		const videoMeta = await collectYouTubeVideoMetadata();
		if (videoMeta) {
			await form.setFieldValue("youtubeVideoMetadata", videoMeta);
		}

		return true;
	}, [
		selectedPlatforms,
		selectedFiles,
		emit,
		form,
		collectYouTubeVideoMetadata,
	]);

	const validateTikTokBeforePublish = useCallback(() => {
		if (!selectedPlatforms.includes(Platform.TIKTOK)) {
			return true;
		}

		const maxCaption = getSocialConstraints(Platform.TIKTOK).message.max;
		if ((form.values.content ?? "").length > maxCaption) {
			emit({
				type: AlertType.ERROR,
				content: `Post copy content must be no longer than ${maxCaption} characters.`,
			});
			return false;
		}

		const tiktokCheck = validateTikTokMedia(
			selectedFiles.map((f) => ({
				extension: f.extension,
				size: f.size,
				type: f.type,
				filename: f.filename,
			})),
		);
		if (!tiktokCheck.valid) {
			emit({
				type: AlertType.ERROR,
				content: tiktokCheck.errors.join(" "),
			});
			return false;
		}
		if (tiktokPublishBlockedReason) {
			emit({
				type: AlertType.ERROR,
				content: tiktokPublishBlockedReason,
			});
			return false;
		}
		return true;
	}, [
		selectedPlatforms,
		selectedFiles,
		form.values.content,
		tiktokPublishBlockedReason,
		emit,
	]);

	const validateXBeforePublish = useCallback(() => {
		if (!selectedPlatforms.includes(Platform.X)) {
			return true;
		}
		const xConstraints = getSocialConstraints(Platform.X);
		const maxCaption = xConstraints.message.max;
		if ((form.values.content ?? "").length > maxCaption) {
			emit({
				type: AlertType.ERROR,
				content: `X posts must be no longer than ${maxCaption} characters. Shorten the copy, then try again.`,
			});
			return false;
		}
		const maxMedia = xConstraints.media.max;
		if (selectedFiles.length > maxMedia) {
			emit({
				type: AlertType.ERROR,
				content:
					"X posts support one image or one video only. Remove extra media and try again.",
			});
			return false;
		}
		if (
			postCopyContainsUrl(form.values.content ?? "") &&
			!entitlements.xLinkPosting
		) {
			emit({
				type: AlertType.ERROR,
				content:
					"This X post includes a link. Activate X Link Posting in Billing, or remove the link.",
			});
			return false;
		}
		return true;
	}, [
		selectedPlatforms,
		selectedFiles,
		emit,
		form.values.content,
		entitlements.xLinkPosting,
	]);

	const handleScheduleClick = useEventCallback(async () => {
		if (!isPlanPaid) {
			open(ModalPaywall, {});

			return;
		}

		if (!validateTikTokBeforePublish()) {
			return;
		}

		if (!validateXBeforePublish()) {
			return;
		}

		if (selectedPlatforms.includes(Platform.TIKTOK)) {
			const maxDuration = tiktokStatus.data.tiktokMaxVideoPostDurationSec;
			if (maxDuration != null) {
				for (const file of selectedFiles.filter(
					(f) => f.type === ContentType.VIDEO,
				)) {
					try {
						const meta = await readVideoMetadataFromUrl(file.url);
						if (meta.durationSec > maxDuration) {
							emit({
								type: AlertType.ERROR,
								content: `This TikTok account can post videos up to ${maxDuration} seconds. Your video is longer.`,
							});
							return;
						}
					} catch {
						// Duration probe is best-effort; TikTok will reject if too long.
					}
				}
			}
		}

		if (!(await validateYouTubeBeforePublish())) {
			return;
		}

		const scheduleSummary: SchedulePostSummary | undefined =
			selectedPlatforms.includes(Platform.YOUTUBE)
				? {
						title:
							form.values.subject.trim() ||
							form.values.content.trim().slice(0, 100) ||
							"Untitled",
						channelName: selectedYoutubeChannel?.name,
						channelImageUrl: selectedYoutubeChannel?.imageUrl,
						privacyLabel: youtubePrivacyLabel(
							form.values.youtubePrivacyStatus ?? "public",
						),
						hasVideo: selectedFiles.some((f) => f.type === "video"),
						estimatedYoutubeUrl: "https://www.youtube.com/shorts/…",
					}
				: undefined;

		const postDate = post?.scheduledAt
			? Instant.parse(post.scheduledAt)
			: Instant.now();

		open(ModalSchedulePost, {
			summary: scheduleSummary,
			requireMinLeadMinutes: selectedPlatforms.includes(Platform.YOUTUBE),
			onConfirm: async (datetime: ZonedDateTime | undefined) => {
				await form.setFieldValue(
					"scheduledAt",
					datetime?.toInstant().toString(),
				);
				await form.setFieldValue("publish", !datetime);
				await form.setFieldValue("status", PostStatus.SCHEDULED);
				await form.submitForm();
			},
			initial: !form.values.publish
				? ZonedDateTime.ofInstant(postDate, ZonedDateTime.now().zone())
				: undefined,
		});
	});

	const handlePublishClick = useEventCallback(async () => {
		if (!isPlanPaid) {
			open(ModalPaywall, {});

			return;
		}

		if (!validateTikTokBeforePublish()) {
			return;
		}

		if (!validateXBeforePublish()) {
			return;
		}

		if (selectedPlatforms.includes(Platform.TIKTOK)) {
			const maxDuration = tiktokStatus.data.tiktokMaxVideoPostDurationSec;
			if (maxDuration != null) {
				for (const file of selectedFiles.filter(
					(f) => f.type === ContentType.VIDEO,
				)) {
					try {
						const meta = await readVideoMetadataFromUrl(file.url);
						if (meta.durationSec > maxDuration) {
							emit({
								type: AlertType.ERROR,
								content: `This TikTok account can post videos up to ${maxDuration} seconds. Your video is longer.`,
							});
							return;
						}
					} catch {
						// Duration probe is best-effort; TikTok will reject if too long.
					}
				}
			}
		}

		if (!(await validateYouTubeBeforePublish())) {
			return;
		}

		const scheduleSummary: SchedulePostSummary | undefined =
			selectedPlatforms.includes(Platform.YOUTUBE)
				? {
						title:
							form.values.subject.trim() ||
							form.values.content.trim().slice(0, 100) ||
							"Untitled",
						channelName: selectedYoutubeChannel?.name,
						channelImageUrl: selectedYoutubeChannel?.imageUrl,
						privacyLabel: youtubePrivacyLabel(
							form.values.youtubePrivacyStatus ?? "public",
						),
						hasVideo: selectedFiles.some((f) => f.type === "video"),
						estimatedYoutubeUrl: "https://www.youtube.com/shorts/…",
					}
				: undefined;

		open(ModalSchedulePost, {
			summary: scheduleSummary,
			requireMinLeadMinutes: selectedPlatforms.includes(Platform.YOUTUBE),
			onConfirm: async (time: ZonedDateTime | undefined) => {
				await form.setFieldValue("scheduledAt", time?.toInstant().toString());
				await form.setFieldValue("publish", !time);
				await form.submitForm();
			},
			initial: undefined,
		});
	});

	const handleSubmitForApproval = useEventCallback(async () => {
		if (!post?.id) return;

		// First save the post to ensure latest changes are persisted
		await update.mutateAsync({
			id: post.id,
			dto: {
				...form.values,
				platform: form.values.platform,
				files: form.values.files,
			},
		});

		// Then submit for approval
		await submitForApproval.mutateAsync(post.id);
		navigate(LinkGen.Calendar());
	});

	const handleApprovePost = useEventCallback(async () => {
		if (!post?.id) return;
		await approvePost.mutateAsync(post.id);
	});

	const handleRejectPost = useEventCallback(async (reason?: string) => {
		if (!post?.id) return;
		await rejectPost.mutateAsync({ id: post.id, reason });
	});

	const contextValues = useMemo(
		(): FormPostContextType => ({
			form,
			editing,
			post,
			siblings,
			platforms,
			selectedPlatforms,
			selectedFiles,
			selectedThumbnail,
			isPlanPaid,
			isBlog,
			needsApproval,
			isShorts,
			tiktokPublishBlockedReason,
			handlePlatformChange,
			handleScheduleClick,
			handlePublishClick,
			handleAttachContent,
			handleRemoveContent,
			handleReorderFiles,
			handleSelectThumbnail,
			handleRemoveThumbnail,
			handleSaveClick,
			isSaving: update.isPending,
			handleSaveDraftClick,
			handleDeleteClick,
			handleSubmitForApproval,
			handleApprovePost,
			handleRejectPost,
		}),
		[
			form,
			editing,
			post,
			siblings,
			platforms,
			selectedPlatforms,
			selectedFiles,
			selectedThumbnail,
			isPlanPaid,
			isBlog,
			needsApproval,
			isShorts,
			tiktokPublishBlockedReason,
			handlePlatformChange,
			handleScheduleClick,
			handlePublishClick,
			handleAttachContent,
			handleRemoveContent,
			handleReorderFiles,
			handleSelectThumbnail,
			handleRemoveThumbnail,
			handleSaveClick,
			update.isPending,
			handleSaveDraftClick,
			handleDeleteClick,
			handleSubmitForApproval,
			handleApprovePost,
			handleRejectPost,
		],
	);
	return (
		<FormPostContext.Provider value={contextValues}>
			{children}
		</FormPostContext.Provider>
	);
};

export const useFormPostContext = () => {
	const context = useContext(FormPostContext);
	if (!context) {
		throw new Error(
			"useFormPostContext must be used within a FormPostProvider",
		);
	}
	return context;
};

function parseQueryMediaIds(
	mediaIdsParam: string | null,
	mediaIdParam: string | null,
): number[] {
	const fromList = (mediaIdsParam ?? "")
		.split(",")
		.map((value) => Number.parseInt(value.trim(), 10))
		.filter((id) => Number.isFinite(id) && id > 0);
	if (fromList.length) {
		return fromList;
	}
	const fromSingle = Number.parseInt(mediaIdParam ?? "", 10);
	return Number.isFinite(fromSingle) && fromSingle > 0 ? [fromSingle] : [];
}

function generatedContentFromState(state: unknown): string {
	if (!state || typeof state !== "object" || Array.isArray(state)) {
		return "";
	}
	const value = (state as { generatedContent?: unknown }).generatedContent;
	return typeof value === "string" ? value : "";
}

function parsePlatformQueryParam(value: string | null): Platform | null {
	if (!value) return null;
	const match = (Object.values(Platform) as string[]).find((p) => p === value);
	return (match as Platform | undefined) ?? null;
}
